import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shadcn-in-nx/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import { useBluelagoon } from "@bluelagoon";
import { Building, Loader2 } from "lucide-react";
import { useManageCompany } from "./ManageCompanyContext";
import Overview from "./Overview";
import UsersTab from "./UsersTab";
import CreditsTab from "./CreditsTab";
import WorkflowsTab from "./WorkflowsTab";
import ChatsTab from "./ChatsTab";
import TicketsTab from "./TicketsTab";
import SettingsTab from "./SettingsTab";
import CompanyPicker from "./CompanyPicker";

export default function ManageCompanyModal() {
    const { t } = useTranslation();
    const { isOpen, companyId, close, refreshKey } = useManageCompany();
    const [activeTab, setActiveTab] = useState("overview");
    const [details, setDetails] = useState(null);
    const { call: fetchDetails, isLoading } = useBluelagoon({ method: "getCompanyDetails" });

    useEffect(() => {
        if (!companyId || !isOpen) {
            setDetails(null);
            return;
        }
        fetchDetails({ companyId })
            .then((res) => setDetails(res?.data || null))
            .catch(() => setDetails(null));
    }, [companyId, isOpen, refreshKey, fetchDetails]);

    const tabs = [
        { value: "overview", label: t("admin.manageCompany.tabs.overview", "Overview") },
        { value: "users", label: t("admin.manageCompany.tabs.users", "Users") },
        { value: "credits", label: t("admin.manageCompany.tabs.credits", "Credits") },
        { value: "workflows", label: t("admin.manageCompany.tabs.workflows", "Workflows") },
        { value: "chats", label: t("admin.manageCompany.tabs.chats", "Chats") },
        { value: "tickets", label: t("admin.manageCompany.tabs.tickets", "Tickets") },
        { value: "settings", label: t("admin.manageCompany.tabs.settings", "Settings") },
    ];

    return (
        <Dialog open={isOpen} onOpenChange={(o) => { if (!o) close(); }}>
            <DialogContent className="inset-0 left-0 top-0 translate-x-0 translate-y-0 w-screen h-screen max-w-none sm:max-w-none rounded-none sm:rounded-none border-0 p-0 gap-0 flex flex-col overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-2 border-b">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        <Building className="h-5 w-5" />
                        {details?.name || t("admin.manageCompany.title", "Manage Company")}
                    </DialogTitle>
                    {companyId && (
                        <p className="text-xs text-muted-foreground font-mono">{companyId}</p>
                    )}
                </DialogHeader>
                {!companyId ? (
                    <div className="flex-1 overflow-y-auto p-6"><CompanyPicker /></div>
                ) : isLoading && !details ? (
                    <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                        <TabsList className="mx-6 mt-2 self-start">
                            {tabs.map((tab) => (
                                <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
                            ))}
                        </TabsList>
                        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
                            <TabsContent value="overview" className="m-0"><Overview details={details} /></TabsContent>
                            <TabsContent value="users" className="m-0"><UsersTab companyId={companyId} /></TabsContent>
                            <TabsContent value="credits" className="m-0"><CreditsTab companyId={companyId} details={details} /></TabsContent>
                            <TabsContent value="workflows" className="m-0"><WorkflowsTab companyId={companyId} /></TabsContent>
                            <TabsContent value="chats" className="m-0"><ChatsTab companyId={companyId} /></TabsContent>
                            <TabsContent value="tickets" className="m-0"><TicketsTab companyId={companyId} /></TabsContent>
                            <TabsContent value="settings" className="m-0"><SettingsTab companyId={companyId} /></TabsContent>
                        </div>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    );
}
